/**
 * MASTER-DATA-FOUNDATION-000001 — Warehouse Master maintenance UI.
 * Search, inline edit, bulk import/export, keyboard-friendly, light/dark.
 * Never invent warehouse geo; Unknown stays Unknown until founder maps.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  WAREHOUSE_STATUSES,
  WAREHOUSE_TYPES,
  warehouseMasterApi,
  type Warehouse,
  type WarehouseStatus,
  type WarehouseType,
} from '../api/warehouseMaster';

const emptyDraft = (code = ''): Partial<Warehouse> => ({
  warehouse_code: code.toUpperCase(),
  warehouse_name: '',
  marketplace: 'A21TJRUUN4KGV',
  country: 'IN',
  state: '',
  city: '',
  region: '',
  warehouse_type: 'amazon_fc',
  status: code ? 'unknown' : 'active',
  remarks: '',
});

export function WarehouseMasterPage() {
  const [params] = useSearchParams();
  const codeFromUrl = (params.get('code') ?? '').trim().toUpperCase();
  const [q, setQ] = useState(codeFromUrl);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [items, setItems] = useState<Warehouse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [editing, setEditing] = useState<Partial<Warehouse> | null>(
    codeFromUrl ? emptyDraft(codeFromUrl) : null,
  );
  const [busy, setBusy] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const formId = useId();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await warehouseMasterApi.list(q, statusFilter, 200, 0);
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (e) {
      setError(String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (codeFromUrl) {
      setEditing(emptyDraft(codeFromUrl));
      setQ(codeFromUrl);
    }
  }, [codeFromUrl]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape' && editing) {
        setEditing(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  async function save() {
    if (!editing?.warehouse_code?.trim()) {
      setError('Warehouse code is required');
      return;
    }
    setBusy(true);
    setError('');
    setMsg('');
    try {
      const payload: Partial<Warehouse> = {
        ...editing,
        warehouse_code: editing.warehouse_code!.toUpperCase().trim(),
        warehouse_type: (editing.warehouse_type as WarehouseType) || 'unknown',
        status: (editing.status as WarehouseStatus) || 'unknown',
      };
      if (editing.id) {
        await warehouseMasterApi.update(editing.id, payload);
      } else {
        await warehouseMasterApi.save(payload);
      }
      setMsg('Saved. Future records resolve automatically.');
      setEditing(null);
      await load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onExport() {
    setError('');
    try {
      const blob = await warehouseMasterApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'warehouse-master.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(String(e));
    }
  }

  async function onImport(file: File) {
    setBusy(true);
    setError('');
    try {
      const r = await warehouseMasterApi.importCsv(file);
      setMsg(`Import: ${r.created} created, ${r.updated} updated, ${r.errors} errors`);
      await load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto max-w-7xl px-3 py-6 sm:px-6">
        <nav className="text-xs text-slate-500 dark:text-slate-400" aria-label="Breadcrumb">
          <Link to="/settings/master-data" className="hover:underline">
            Settings · Master Data
          </Link>
          <span className="mx-1.5">/</span>
          <span>Warehouse Master</span>
        </nav>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Warehouse Master</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Map Amazon codes (BOM7, MAA4…) to business names. Unknown never breaks processing.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="min-h-11 cursor-pointer rounded-md border border-slate-300 bg-white px-3 text-sm font-medium dark:border-slate-600 dark:bg-slate-900"
              onClick={() => setEditing(emptyDraft())}
            >
              Add mapping
            </button>
            <button
              type="button"
              className="min-h-11 cursor-pointer rounded-md border border-slate-300 bg-white px-3 text-sm font-medium dark:border-slate-600 dark:bg-slate-900"
              onClick={() => void onExport()}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="min-h-11 cursor-pointer rounded-md border border-slate-300 bg-white px-3 text-sm font-medium dark:border-slate-600 dark:bg-slate-900"
              onClick={() => fileRef.current?.click()}
            >
              Import CSV
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onImport(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor={`${formId}-q`}>
            Search warehouses
          </label>
          <input
            id={`${formId}-q`}
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load();
            }}
            placeholder="Search code, city, state… (Ctrl+K)"
            className="min-h-11 min-w-[14rem] flex-1 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-600 dark:bg-slate-900"
          />
          <label className="sr-only" htmlFor={`${formId}-st`}>
            Status
          </label>
          <select
            id={`${formId}-st`}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="min-h-11 rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-600 dark:bg-slate-900"
          >
            <option value="">All statuses</option>
            {WAREHOUSE_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="min-h-11 cursor-pointer rounded-md bg-slate-900 px-4 text-sm font-medium text-white dark:bg-sky-600"
            onClick={() => void load()}
          >
            Search
          </button>
          <span className="text-xs text-slate-500">{total} records</span>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
        {msg && (
          <p role="status" className="mt-3 text-sm text-emerald-700 dark:text-emerald-400">
            {msg}
          </p>
        )}

        {editing && (
          <div
            className="mt-6 rounded-lg border border-sky-200 bg-white p-4 dark:border-sky-900 dark:bg-slate-900"
            role="region"
            aria-label="Edit warehouse mapping"
          >
            <h2 className="text-sm font-semibold">
              {editing.id ? 'Edit mapping' : 'Add mapping'}
              {editing.status === 'unknown' || !editing.id ? (
                <span className="ml-2 text-xs font-normal text-amber-700 dark:text-amber-400">
                  Unknown until saved with city/state
                </span>
              ) : null}
            </h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Warehouse code *" htmlFor={`${formId}-code`}>
                <input
                  id={`${formId}-code`}
                  className="field-in"
                  value={editing.warehouse_code ?? ''}
                  onChange={(e) => setEditing({ ...editing, warehouse_code: e.target.value.toUpperCase() })}
                  disabled={!!editing.id}
                  required
                />
              </Field>
              <Field label="Warehouse name" htmlFor={`${formId}-name`}>
                <input
                  id={`${formId}-name`}
                  className="field-in"
                  value={editing.warehouse_name ?? ''}
                  onChange={(e) => setEditing({ ...editing, warehouse_name: e.target.value })}
                />
              </Field>
              <Field label="City" htmlFor={`${formId}-city`}>
                <input
                  id={`${formId}-city`}
                  className="field-in"
                  value={editing.city ?? ''}
                  onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                  placeholder="Only if known — never guess"
                />
              </Field>
              <Field label="State" htmlFor={`${formId}-state`}>
                <input
                  id={`${formId}-state`}
                  className="field-in"
                  value={editing.state ?? ''}
                  onChange={(e) => setEditing({ ...editing, state: e.target.value })}
                />
              </Field>
              <Field label="Region" htmlFor={`${formId}-region`}>
                <input
                  id={`${formId}-region`}
                  className="field-in"
                  value={editing.region ?? ''}
                  onChange={(e) => setEditing({ ...editing, region: e.target.value })}
                />
              </Field>
              <Field label="Marketplace" htmlFor={`${formId}-mp`}>
                <input
                  id={`${formId}-mp`}
                  className="field-in"
                  value={editing.marketplace ?? ''}
                  onChange={(e) => setEditing({ ...editing, marketplace: e.target.value })}
                />
              </Field>
              <Field label="Country" htmlFor={`${formId}-co`}>
                <input
                  id={`${formId}-co`}
                  className="field-in"
                  value={editing.country ?? ''}
                  onChange={(e) => setEditing({ ...editing, country: e.target.value })}
                />
              </Field>
              <Field label="Type" htmlFor={`${formId}-type`}>
                <select
                  id={`${formId}-type`}
                  className="field-in"
                  value={editing.warehouse_type ?? 'unknown'}
                  onChange={(e) =>
                    setEditing({ ...editing, warehouse_type: e.target.value as WarehouseType })
                  }
                >
                  {WAREHOUSE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status" htmlFor={`${formId}-status`}>
                <select
                  id={`${formId}-status`}
                  className="field-in"
                  value={editing.status ?? 'unknown'}
                  onChange={(e) =>
                    setEditing({ ...editing, status: e.target.value as WarehouseStatus })
                  }
                >
                  {WAREHOUSE_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Latitude (optional)" htmlFor={`${formId}-lat`}>
                <input
                  id={`${formId}-lat`}
                  type="number"
                  step="any"
                  className="field-in"
                  value={editing.latitude ?? ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      latitude: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </Field>
              <Field label="Longitude (optional)" htmlFor={`${formId}-lon`}>
                <input
                  id={`${formId}-lon`}
                  type="number"
                  step="any"
                  className="field-in"
                  value={editing.longitude ?? ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      longitude: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </Field>
              <Field label="Remarks" htmlFor={`${formId}-rm`}>
                <input
                  id={`${formId}-rm`}
                  className="field-in"
                  value={editing.remarks ?? ''}
                  onChange={(e) => setEditing({ ...editing, remarks: e.target.value })}
                />
              </Field>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                className="min-h-11 cursor-pointer rounded-md bg-sky-700 px-4 text-sm font-medium text-white disabled:opacity-50 dark:bg-sky-600"
                onClick={() => void save()}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="min-h-11 cursor-pointer rounded-md border border-slate-300 px-4 text-sm dark:border-slate-600"
                onClick={() => setEditing(null)}
              >
                Cancel (Esc)
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[56rem] text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2.5">Code</th>
                <th className="px-3 py-2.5">Name</th>
                <th className="px-3 py-2.5">City</th>
                <th className="px-3 py-2.5">State</th>
                <th className="px-3 py-2.5">Region</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                    No warehouses mapped yet. Add a mapping or import CSV.
                  </td>
                </tr>
              )}
              {items.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-slate-100 dark:border-slate-800"
                  data-status={row.status}
                >
                  <td className="px-3 py-2 font-mono font-medium">{row.warehouse_code}</td>
                  <td className="px-3 py-2">{row.warehouse_name || '—'}</td>
                  <td className="px-3 py-2">{row.city || '—'}</td>
                  <td className="px-3 py-2">{row.state || '—'}</td>
                  <td className="px-3 py-2">{row.region || '—'}</td>
                  <td className="px-3 py-2">{row.warehouse_type}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="min-h-11 cursor-pointer text-sky-700 underline-offset-2 hover:underline dark:text-sky-400"
                      onClick={() => setEditing({ ...row })}
                    >
                      {row.status === 'unknown' ? 'Add mapping' : 'Edit'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`
        .field-in {
          width: 100%;
          min-height: 2.75rem;
          border-radius: 0.375rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0 0.75rem;
          font-size: 0.875rem;
        }
        .dark .field-in {
          border-color: rgb(71 85 105);
          background: rgb(15 23 42);
          color: rgb(241 245 249);
        }
      `}</style>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function StatusBadge({ status }: { status: WarehouseStatus }) {
  const styles =
    status === 'unknown'
      ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
      : status === 'inactive'
        ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
        : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200';
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${styles}`}>
      {status}
    </span>
  );
}
